/**
 * Evidence Governance
 * ===================
 * Evidence collection, validation, and audit standards.
 * 
 * Route: /wyldecrest/helpdesk/evidence-governance
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  FileCheck, 
  Camera, 
  Shield, 
  AlertTriangle,
  Clock,
  CheckCircle
} from 'lucide-react';

const sections = [
  { id: 'evidence-standards', title: 'Evidence Standards' },
  { id: 'photo-requirements', title: 'Photo Requirements' },
  { id: 'document-requirements', title: 'Document Requirements' },
  { id: 'validation-process', title: 'Validation Process' },
  { id: 'audit-trail', title: 'Audit Trail Requirements' },
];

export default function EvidenceGovernancePage() {
  return (
    <GovernancePageLayout
      title="Evidence Governance"
      subtitle="Standards for evidence collection, validation, and audit compliance"
      icon={FileCheck}
      version="2.1"
      lastReviewed="2026-05-22"
      reviewedBy="Compliance Manager"
      sections={sections}
    >
      {/* Evidence Standards */}
      <DocumentSection title="Evidence Standards" icon={Shield} id="evidence-standards">
        <p className="text-slate-300 mb-6">
          All jobs require evidence to support completion claims and audit requirements.
          Evidence governance ensures consistency, quality, and compliance.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Evidence Categories</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-semibold mb-2">Pre-Work Evidence</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Photos of condition before work</li>
              <li>• Site access confirmation</li>
              <li>• Risk assessment (if required)</li>
              <li>• Customer sign-in record</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">During Work Evidence</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Progress photos</li>
              <li>• Any unforeseen issues documented</li>
              <li>• Parts/materials used</li>
              <li>• Time records</li>
            </ul>
          </div>
          
          <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
            <h4 className="text-purple-300 font-semibold mb-2">Post-Work Evidence</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Completed work photos</li>
              <li>• Certificates issued</li>
              <li>• Customer sign-off</li>
              <li>• Warranty documentation</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Compliance Evidence</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Gas Safe certificates</li>
              <li>• Electrical certificates</li>
              <li>• Test reports</li>
              <li>• Compliance declarations</li>
            </ul>
          </div>
        </div>

        <WarningBox title="Mandatory Evidence">
          Jobs cannot transition to "Completed" status without valid completion evidence.
          The system enforces this requirement automatically.
        </WarningBox>
      </DocumentSection>

      {/* Photo Requirements */}
      <DocumentSection title="Photo Requirements" icon={Camera} id="photo-requirements">
        <p className="text-slate-300 mb-6">
          Photos must meet minimum quality standards to be accepted as valid evidence.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Photo Quality Standards</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Requirement</th>
                <th className="pb-2">Standard</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Resolution</td>
                <td>Minimum 1280x720 pixels</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Lighting</td>
                <td>Subject clearly visible, no excessive shadows</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Focus</td>
                <td>Work area in sharp focus</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Framing</td>
                <td>Full work area visible with context</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Timestamp</td>
                <td>EXIF data required (automatic from device)</td>
              </tr>
              <tr>
                <td className="py-2">File Format</td>
                <td>JPEG, PNG, or HEIC</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Minimum Photo Count by Job Type</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Job Type</th>
                <th className="pb-2">Before</th>
                <th className="pb-2">During</th>
                <th className="pb-2">After</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Reactive Repair</td>
                <td>2</td>
                <td>Optional</td>
                <td>2</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">PPM Service</td>
                <td>1</td>
                <td>1</td>
                <td>1</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Quoted Works</td>
                <td>4</td>
                <td>2</td>
                <td>4</td>
              </tr>
              <tr>
                <td className="py-2">Major Project</td>
                <td>6</td>
                <td>6</td>
                <td>6</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Checklist 
          title="Photo Submission Checklist"
          items={[
            'Photos taken in good lighting conditions',
            'Work area clearly identifiable',
            'Before and after angles match',
            'Any defects/issues visible in before photos',
            'Completed work clearly visible in after photos',
            'No personal information visible (blur if needed)',
            'Device timestamp enabled'
          ]}
        />
      </DocumentSection>

      {/* Document Requirements */}
      <DocumentSection title="Document Requirements" icon={FileCheck} id="document-requirements">
        <p className="text-slate-300 mb-6">
          Certain job types require specific documentation for compliance.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Required Documents by Trade</h3>
        
        <div className="space-y-4">
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
            <h4 className="text-red-300 font-semibold mb-2">Gas Work</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Gas Safe certificate (CP12 for landlord checks)</li>
              <li>• Warning notice (if appliance condemned)</li>
              <li>• Benchmark commissioning checklist (new installs)</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Electrical Work</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• EICR (Periodic inspection)</li>
              <li>• Minor works certificate</li>
              <li>• Part P notification (where applicable)</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Fire Safety</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Fire alarm test certificate</li>
              <li>• Emergency lighting test record</li>
              <li>• Fire door inspection report</li>
            </ul>
          </div>
          
          <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
            <h4 className="text-purple-300 font-semibold mb-2">Water/Legionella</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Legionella risk assessment</li>
              <li>• Temperature monitoring logs</li>
              <li>• Water treatment records</li>
            </ul>
          </div>
        </div>
      </DocumentSection>

      {/* Validation Process */}
      <DocumentSection title="Validation Process" icon={CheckCircle} id="validation-process">
        <p className="text-slate-300 mb-6">
          All evidence must be validated before a job can be marked complete.
        </p>

        <SOPStep 
          number={1}
          title="Automated Checks"
          description="System performs initial validation on upload."
          substeps={[
            'File format validation',
            'Minimum resolution check',
            'EXIF timestamp extraction',
            'File size within limits'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Completeness Check"
          description="Verify all required evidence types are present."
          substeps={[
            'Check before photos uploaded',
            'Check after photos uploaded',
            'Check required documents attached',
            'Verify photo/document count meets minimum'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Quality Review"
          description="Manual review of evidence quality."
          substeps={[
            'Photos are clear and identifiable',
            'Before/after photos match location',
            'Work completion is visible',
            'Documents are legible and complete'
          ]}
        />

        <SOPStep 
          number={4}
          title="Approval or Return"
          description="Finalize evidence review."
          substeps={[
            'If complete: Approve job for completion',
            'If incomplete: Return with specific feedback',
            'If unclear: Request additional evidence',
            'Document review decision in audit log'
          ]}
        />

        <InfoBox title="Evidence Lock">
          Once evidence is approved and job is closed, evidence cannot be modified.
          Any changes require raising a new job or formal amendment process.
        </InfoBox>
      </DocumentSection>

      {/* Audit Trail */}
      <DocumentSection title="Audit Trail Requirements" icon={Clock} id="audit-trail">
        <p className="text-slate-300 mb-6">
          All evidence-related actions are logged for audit and compliance purposes.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Audit Events Logged</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Event</th>
                <th className="pb-2">Data Captured</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Evidence Upload</td>
                <td>User, timestamp, file hash, file type</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Evidence Review</td>
                <td>Reviewer, timestamp, decision, notes</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Evidence Rejection</td>
                <td>Reviewer, timestamp, reason, feedback</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Evidence Approval</td>
                <td>Reviewer, timestamp, approval notes</td>
              </tr>
              <tr>
                <td className="py-2">Evidence Download</td>
                <td>User, timestamp, purpose</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Retention Policy</h3>
        
        <ul className="text-slate-300 space-y-2 ml-4">
          <li>• <strong className="text-white">Active Jobs</strong>: Evidence retained indefinitely</li>
          <li>• <strong className="text-white">Closed Jobs</strong>: 7 years from completion</li>
          <li>• <strong className="text-white">Compliance Documents</strong>: 10 years from expiry</li>
          <li>• <strong className="text-white">Audit Logs</strong>: Permanent retention</li>
        </ul>
      </DocumentSection>
    </GovernancePageLayout>
  );
}
